import { ReactElement, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Col, notification, Row, TablePaginationConfig, Tag, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import EnhancedTable, { ColumnType } from 'app/molecules/EnhancedTable';
import { FilterFilled, PlusOutlined, DeleteOutlined, EditOutlined, PictureOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { handleTableParams } from 'utils/tableConfig';
import { renderDateFilterDropdown } from '../../../utils/dateFilterUtils';
import { buildTableParams } from 'utils/misc';
import dayjs from 'dayjs';
import GlobalSearchBar from 'app/atoms/GlobalSearchBar';
import { ErrorMessage, getErrorMessage } from 'utils/axios';
import { useUserInfo } from 'app/hooks/useUserInfo';
import { BANNER_CONFIG } from 'configs/routeNames';
import StatusTag, { StatusType } from 'app/atoms/StatusTag';
import Modal from 'app/atoms/Modal';
import { IBannerTableItem, IBannerTableProps, IBannerTableResponse, IBannerTranslation } from './types';
import { deleteBanner, searchBanners } from './services';
import { fieldDefaultProps } from 'utils/fieldConfig';
import { NotificationProps } from 'utils/types';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { DATE_FORMAT } from 'configs/constants';
import { Authorize } from 'app/atoms';
import { PermissionActions } from '../Auth/types';
import { PERMISSIONS_SUBJECTS } from '../Auth/constants';
import { usePermissions } from 'app/hooks/usePermissions';

const BannerTable = ({ showButton = true, showSearch = true }: IBannerTableProps): ReactElement => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams({ page: '0', size: '10' });
  const [filteredColumnsInfo, setFilteredColumnsInfo] = useState<Record<string, string>>({});
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState<IBannerTableItem | null>(null);

  const { data: currentUser } = useUserInfo();
  const { agency } = currentUser || {};
  const agencyId = agency?.id ?? '';

  const { can } = usePermissions();

  // Initialize filteredColumnsInfo with URL parameters and handle language changes
  useEffect(() => {
    const initialFilteredColumnsInfo: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (value) {
        initialFilteredColumnsInfo[key] = value;
      }
    });
    setFilteredColumnsInfo(initialFilteredColumnsInfo);
  }, [searchParams, i18n.language]);

  const { isLoading, isFetching, data } = useQuery<IBannerTableResponse>({
    queryKey: ['banners', searchParams.toString(), agencyId],
    queryFn: () => {
      const params = buildTableParams(searchParams, {}, {});

      return searchBanners(agencyId, params);
    },
    enabled: Boolean(agencyId) && can(PermissionActions.VIEW_BANNERS, PERMISSIONS_SUBJECTS.BANNERS),
  });

  const deleteMutation = useMutation({
    mutationKey: ['delete-banner'],
    mutationFn: async (id: string) => {
      return deleteBanner(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banners'], exact: false });
      notification.success({
        message: t('apiSuccessMessages.deleteBanner'),
        ...NotificationProps,
      });
    },
    onError: (error: ErrorMessage) => {
      notification.error({
        message: getErrorMessage(error),
        ...NotificationProps,
      });
    },
  });

  const handleBackToList = () => {
    queryClient.setQueryData(['header-props'], {
      selectedMenuItem: t('sideMenu.banners'),
    });

    if (window.history.state && window.history.state.idx > 0) {
      // using the browser route history and go back from where you came from - stack memory
      navigate(-1);
    } else {
      // default case
      navigate(`/${BANNER_CONFIG}`);
    }
  };

  const onCreateClick = () => {
    navigate(`/${BANNER_CONFIG}/create`);
    queryClient.setQueryData(['header-props'], {
      selectedMenuItem: t('sideMenu.banners'),
      onBack: handleBackToList,
      detailTitle: t('banner.form.create.title'),
    });
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IBannerTableItem> | SorterResult<IBannerTableItem>[],
  ) => {
    handleTableParams(pagination, filters, sorter, searchParams, setSearchParams);
  };

  const handleTableSearch = (value: string, key: string) => {
    searchParams.set(key, value);
    searchParams.set('page', '0');
    searchParams.set('size', '10');
    setSearchParams(searchParams);
  };

  const onView = (record: IBannerTableItem) => {
    navigate(`/${BANNER_CONFIG}/edit/${record.id}`);
  };

  const onEdit = (record: IBannerTableItem) => {
    navigate(`/${BANNER_CONFIG}/edit/${record.id}`);
  };

  const onDelete = (record: IBannerTableItem) => {
    setBannerToDelete(record);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    if (bannerToDelete) {
      deleteMutation.mutate(bannerToDelete.id.toString());
      setDeleteModalVisible(false);
      setBannerToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setBannerToDelete(null);
  };

  queryClient.setQueryData(['header-props'], {
    selectedMenuItem: t('sideMenu.banners'),
  });

  const columns: ColumnType<IBannerTableItem>[] = [
    {
      dataIndex: 'imageUrl',
      title: t('banner.table.dataColumns.translatedImage'),
      searchable: false,
      width: 250,
      className: 'relative',
      render: (imageUrl: string) =>
        imageUrl ? (
          <img
            src={imageUrl}
            alt={t('banner.table.imageAlt')}
            loading="lazy"
            className="object-cover w-[220px] h-[30px] rounded-lg"
          />
        ) : (
          <PictureOutlined className="text-2xl text-gray-600 flex justify-center" />
        ),
    },
    {
      dataIndex: 'title',
      title: t('banner.table.dataColumns.title'),
      searchable: false,
      width: 400,
      className: 'relative',
      render: (title: string) => (
        <div className="truncate-text">
          <Tooltip placement="topLeft" title={title ?? ''}>
            {title ?? t('N/A')}
          </Tooltip>
        </div>
      ),
    },

    {
      dataIndex: 'translations',
      title: t('banner.table.dataColumns.currentTranslations'),
      searchable: false,
      width: 150,
      ellipsis: true,
      render: (_: unknown, record: IBannerTableItem) => {
        const translations = Array.isArray(record.translations)
          ? record.translations.filter((tr) => tr && typeof tr === 'object' && 'languageCode' in tr)
          : [];
        const languages = translations.map((tr: IBannerTranslation) => tr.languageCode);
        const maxToShow = 3;
        const displayed = languages.slice(0, maxToShow);
        const extra = languages.length - maxToShow;
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 120 }}>
            {displayed.map((lang) => (
              <Tag
                key={lang}
                color="blue"
                style={{ marginBottom: 2, maxWidth: 40, overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                <Tooltip title={lang}>{lang}</Tooltip>
              </Tag>
            ))}
            {extra > 0 && <Tag color="default">+{extra}</Tag>}
          </div>
        );
      },
    },
    {
      dataIndex: 'status',
      title: t('banner.table.dataColumns.status'),
      searchable: false,
      width: 100,
      ellipsis: true,
      render: (status: StatusType) => (
        <div className="-ml-4">
          <StatusTag status={status} />
        </div>
      ),
    },
    {
      dataIndex: 'displayStartDate',
      title: t('banner.table.dataColumns.dates'),
      searchable: false,
      filterIcon: () => <FilterFilled />,
      width: 150,
      ellipsis: true,
      filterDropdown: renderDateFilterDropdown('displayStartDate', searchParams, setSearchParams, t),
      render: (_, record) => {
        const start = dayjs(record.displayStartDate).format(DATE_FORMAT);
        const end = dayjs(record.displayEndDate).format(DATE_FORMAT);
        return `${start} to ${end}`;
      },
    },
  ];

  const actions = [
    ...(can(PermissionActions.EDIT_BANNERS, PERMISSIONS_SUBJECTS.BANNERS)
      ? [
          {
            key: 'edit',
            label: () => t('banner.table.actions.edit'),
            icon: <EditOutlined />,
            onClick: onEdit,
          },
        ]
      : []),
    ...(can(PermissionActions.DELETE_BANNERS, PERMISSIONS_SUBJECTS.BANNERS)
      ? [
          {
            key: 'delete',
            label: () => t('banner.table.actions.delete'),
            icon: <DeleteOutlined />,
            onClick: onDelete,
            danger: true,
          },
        ]
      : []),
  ];

  return (
    <>
      {showSearch && <GlobalSearchBar />}
      <Card>
        <div className="flex flex-col gap-7">
          <Row align="middle" justify="space-between" className="w-full items-baseline">
            <Col>
              <p className="h4-bold" data-testid="banners-title">
                {t('banner.table.title')}
              </p>
            </Col>
            <Authorize
              requiredPermissions={[{ action: PermissionActions.ADD_BANNERS, subject: PERMISSIONS_SUBJECTS.BANNERS }]}
            >
              <Col className="mt-2 sm:mt-0">
                {showButton && (
                  <Button
                    color="primary"
                    icon={<PlusOutlined />}
                    variant="outlined"
                    data-testid="add-banner-button"
                    size="large"
                    className="bg-blue-600 h5-semibold"
                    onClick={onCreateClick}
                  >
                    {t('banner.addBanner')}
                  </Button>
                )}
              </Col>
            </Authorize>
          </Row>
          <EnhancedTable
            columns={columns}
            dataSource={data?.content}
            rowKey="id"
            loading={isLoading || isFetching}
            sortAndFilterInfo={Object.fromEntries(searchParams)}
            onEdit={() => {}}
            onRemove={() => {}}
            onView={() => {}}
            onChange={handleTableChange}
            onSearch={handleTableSearch}
            onRow={(record) => ({
              onClick: () => {
                onView(record);
              },
            })}
            showSearch={true}
            showSort={true}
            showView={false}
            showEdit={false}
            showRemove={false}
            showMenu={true}
            actions={actions}
            pageInfo={data?.pageInfo}
            data-testid="banner-table"
            setFilteredColumnsInfo={setFilteredColumnsInfo}
            filteredColumnsInfo={filteredColumnsInfo}
            breakpoint="xxl"
          />
        </div>
      </Card>
      <Modal
        isOpen={deleteModalVisible}
        submitText={t('common.confirm')}
        cancelText={t('common.cancel')}
        onClose={handleDeleteCancel}
        onSubmit={handleDeleteConfirm}
        dataTestid="delete-banner-modal"
        closable={false}
        footer={
          <div className="delete-modal-footer flex gap-4 justify-center">
            <Button
              {...fieldDefaultProps}
              variant="outlined"
              color="primary"
              onClick={handleDeleteCancel}
              className="delete-modal-btn w-[150px]"
            >
              {t('banner.table.deleteModal.cancel')}
            </Button>
            <Button
              {...fieldDefaultProps}
              type="primary"
              onClick={handleDeleteConfirm}
              className="delete-modal-btn w-[150px]"
            >
              {t('banner.table.deleteModal.confirm')}
            </Button>
          </div>
        }
      >
        <span className="h4-bold text-primary-600 flex items-center justify-center text-center mt-6">
          {t('banner.table.deleteModal.description')}
        </span>
      </Modal>
    </>
  );
};

export default BannerTable;
